import Hero from "@/components/Hero";
import { Link } from "react-router-dom";
import { IMG, SITE } from "@/lib/api";
import SectionHeader from "@/components/SectionHeader";
import { ArrowUpRight, Home as HomeIcon, Compass, FileSignature, Users, Lock, Phone } from "lucide-react";

const PILLARS = [
  {
    to: "/lifestyles",
    label: "Lifestyles",
    desc: "Selling homes across LA — beach, hills, canyons, & city.",
    Icon: HomeIcon,
    testid: "pillar-lifestyles",
  },
  {
    to: "/coaching",
    label: "Coaching",
    desc: "1:1 mentorship for agents ready to scale with intention.",
    Icon: Compass,
    testid: "pillar-coaching",
  },
  {
    to: "/escrow",
    label: "Escrow / Netsheets",
    desc: "Your agent escrow partner. Faster closings, digital tools, and instant netsheets.",
    Icon: FileSignature,
    testid: "pillar-escrow",
  },
  {
    to: "/resources",
    label: "Resources",
    desc: "Free guides, checklists, and market intel for LA buyers & sellers.",
    Icon: HomeIcon,
    testid: "pillar-resources",
  },
  {
    to: "/community",
    label: "Events",
    desc: "Where we show up — events, gatherings, and the LA community we serve.",
    Icon: Users,
    testid: "pillar-community",
  },
];

const PILLAR_THEMES = [
  { bg: "linear-gradient(160deg, #1e4d5d 0%, #2c5865 60%, #3b7080 100%)", text: "#ffffff", soft: "rgba(255,255,255,0.82)", icon: "#e8c79a", chip: "rgba(232,199,154,0.18)" },
  { bg: "linear-gradient(160deg, #fbe9cf 0%, #f3d3a6 100%)", text: "#1e4d5d", soft: "#2c5865", icon: "#1e4d5d", chip: "rgba(255,255,255,0.6)" },
  { bg: "linear-gradient(160deg, #dcebe8 0%, #bfdad6 100%)", text: "#1e4d5d", soft: "#2c5865", icon: "#b8814f", chip: "rgba(255,255,255,0.65)" },
  { bg: "linear-gradient(160deg, #f6dcd0 0%, #eec2ae 100%)", text: "#1e4d5d", soft: "#2c5865", icon: "#1e4d5d", chip: "rgba(255,255,255,0.6)" },
  { bg: "linear-gradient(160deg, #e8c79a 0%, #d4a373 55%, #b8814f 100%)", text: "#1e4d5d", soft: "rgba(30,77,93,0.9)", icon: "#1e4d5d", chip: "rgba(255,255,255,0.35)" },
];

export default function Home() {
  return (
    <div data-testid="home-page" className="pt-8">
      <Hero />

      {/* Team phone banner — one number, one team */}
      <section
        className="py-14 md:py-20 text-center text-white"
        style={{ backgroundColor: "#1e4d5d", backgroundImage: "radial-gradient(ellipse at top, rgba(212,163,115,0.18), transparent 60%)" }}
        data-testid="team-phone-banner"
      >
        <div className="container-edge">
          <p className="overline" style={{ color: "#e8c79a" }}>Two agents · One phone number</p>
          <p className="mt-6 text-sm md:text-base text-white/80 uppercase tracking-[0.28em]">
            Call the team
          </p>
          <a
            href={SITE.teamPhoneHref}
            className="mt-4 block group"
            data-testid="team-phone-link"
          >
            <span
              className="inline-flex items-center gap-4 md:gap-6 text-white group-hover:text-[#e8c79a] transition-colors"
              style={{
                fontFamily: "Playfair Display, serif",
                fontSize: "clamp(2.5rem, 7vw, 5.5rem)",
                lineHeight: 1.05,
                letterSpacing: "-0.01em",
              }}
            >
              <Phone
                strokeWidth={1.25}
                className="text-[#e8c79a] shrink-0"
                style={{ width: "clamp(28px, 4vw, 56px)", height: "clamp(28px, 4vw, 56px)" }}
              />
              <span data-testid="team-phone-number">{SITE.teamPhone}</span>
            </span>
          </a>
          <p className="mt-8 text-white/85 max-w-xl mx-auto text-sm md:text-base">
            Reach both of us at the same line — day or night. Whether you're buying, selling,
            or just weighing your next move, we'd love to talk.
          </p>
          <div className="mt-8 inline-flex flex-wrap justify-center gap-3">
            <a href={SITE.teamPhoneHref} className="btn-secondary" data-testid="team-phone-call-btn">
              Call {SITE.teamPhone}
            </a>
            <a href={`sms:${SITE.teamPhoneHref.replace('tel:', '')}`} className="btn-outline border-white/70 text-white hover:bg-white hover:text-ocean" data-testid="team-phone-text-btn">
              Text us
            </a>
          </div>
        </div>
      </section>

      {/* Pillars grid (asymmetric bento) */}
      <section
        className="section relative overflow-hidden"
        style={{ background: "linear-gradient(180deg, #f8f5ee 0%, #fdf1de 55%, #f8e6cf 100%)" }}
        data-testid="home-pillars"
      >
        <div aria-hidden="true" className="pointer-events-none absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full" style={{ background: "radial-gradient(circle, rgba(232,199,154,0.55), transparent 70%)" }} />
        <div aria-hidden="true" className="pointer-events-none absolute -bottom-40 -left-32 w-[520px] h-[520px] rounded-full" style={{ background: "radial-gradient(circle, rgba(44,88,101,0.18), transparent 70%)" }} />
        <div className="relative">
        <SectionHeader
          overline="What we offer"
          title="Five doors to your California life."
          subtitle="From your first showing to your hundredth coaching call — here's how we work together."
          testid="pillars-header"
        />

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {PILLARS.map(({ to, label, desc, Icon, testid, external }, idx) => {
            const t = PILLAR_THEMES[idx % PILLAR_THEMES.length];
            const cardClass = `relative overflow-hidden p-8 md:p-10 flex flex-col group shadow-[0_18px_40px_-24px_rgba(30,77,93,0.45)] transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-[0_28px_60px_-24px_rgba(30,77,93,0.55)] ${
              idx === 0 || idx === 4 ? "md:row-span-2 md:min-h-[420px]" : "md:min-h-[200px]"
            }`;
            const inner = (
              <>
                <span aria-hidden="true" className="absolute -right-10 -top-10 w-40 h-40 rounded-full transition-transform duration-700 group-hover:scale-125" style={{ background: t.chip }} />
                <span className="relative inline-flex items-center justify-center w-14 h-14 rounded-full" style={{ background: t.chip }}>
                  <Icon size={26} strokeWidth={1.4} style={{ color: t.icon }} />
                </span>
                <h3 style={{ fontFamily: "Playfair Display, serif", color: t.text }} className="relative mt-8 text-2xl md:text-3xl">
                  {label}
                </h3>
                <p className="relative mt-4 text-sm leading-relaxed flex-1" style={{ color: t.soft }}>{desc}</p>
                <span className="relative mt-8 inline-flex items-center text-sm font-medium transition-transform duration-300 group-hover:translate-x-1" style={{ color: t.text }}>
                  Explore <ArrowUpRight size={14} className="ml-2" />
                </span>
              </>
            );
            return external ? (
              <a
                key={to}
                href={to}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={testid}
                className={cardClass}
                style={{ background: t.bg }}
              >
                {inner}
              </a>
            ) : (
              <Link key={to} to={to} data-testid={testid} className={cardClass} style={{ background: t.bg }}>
                {inner}
              </Link>
            );
          })}
        </div>
        </div>
      </section>

      {/* Lifestyles preview */}
      <section className="section bg-linen" data-testid="home-lifestyle">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-5">
            <p className="overline">Lifestyles</p>
            <h2
              className="mt-4 text-4xl sm:text-5xl lg:text-6xl"
              style={{ fontFamily: "Playfair Display, serif" }}
            >
              Sun, salt, and a key in the door.
            </h2>
            <p className="mt-6 text-soft text-base leading-relaxed">
              LA is not one place — it's a hundred. We help you find the one that fits the life
              you're actually living. Or the one you're ready to step into.
            </p>
            <Link to="/lifestyles" className="btn-outline mt-8" data-testid="home-lifestyle-btn">
              Browse neighborhoods
            </Link>
          </div>
          <div className="lg:col-span-7 grid grid-cols-2 gap-4 md:gap-6">
            <div className="zoom-img col-span-2 md:col-span-1 md:row-span-2 h-[280px] md:h-[480px]">
              <img src={IMG.lifestyle1} alt="LA beach" className="w-full h-full object-cover" />
            </div>
            <div className="zoom-img h-[220px]">
              <img src={IMG.interior1} alt="Coastal interior" className="w-full h-full object-cover" />
            </div>
            <div className="zoom-img h-[220px]">
              <img src={IMG.lifestyle2} alt="LA palms" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Client library teaser */}
      <section className="section" data-testid="home-library-teaser">
        <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch">
          <div className="lg:col-span-7 zoom-img min-h-[400px]">
            <img src={IMG.interior2} alt="Luxury loft" className="w-full h-full object-cover" />
          </div>
          <div className="lg:col-span-5 bg-paper border border-soft p-10 md:p-14 flex flex-col justify-center">
            <Lock className="text-sand" size={28} strokeWidth={1.25} />
            <p className="overline mt-6">Current Clients</p>
            <h3 style={{ fontFamily: "Playfair Display, serif" }} className="mt-3 text-3xl md:text-4xl">
              Your Resource Library
            </h3>
            <p className="mt-6 text-soft leading-relaxed">
              Active clients get private access to checklists, market reports, escrow timelines,
              and our vetted vendor list — all in one place.
            </p>
            <Link to="/client-library" className="btn-primary mt-8 self-start" data-testid="home-library-btn">
              Enter library
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section bg-ocean text-white" style={{ backgroundColor: "var(--primary)" }} data-testid="home-final-cta">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <p className="overline" style={{ color: "#e8c79a" }}>Champagne &amp; Sunshine · Real Estate Team</p>
            <h2 className="mt-4 text-4xl md:text-5xl lg:text-6xl text-white" style={{ fontFamily: "Playfair Display, serif" }}>
              Whether it's a first home or a fresh chapter — let's begin.
            </h2>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link to="/contact" className="btn-secondary" data-testid="home-final-cta-btn">
              Get in touch
            </Link>
            <p className="mt-6 text-white/90 text-sm">{SITE.teamPhone}</p>
            <p className="mt-1 text-white/90 text-sm break-all">
              <a href={SITE.emailHref} className="hover:text-white transition-colors">{SITE.email}</a>
            </p>
            <p className="mt-1 text-white/90 text-sm break-all">
              <a href={SITE.escrowEmailHref} className="hover:text-white transition-colors" data-testid="home-cta-escrow-email">{SITE.escrowEmail}</a>
            </p>
            <p className="mt-3 text-white/75 text-xs uppercase tracking-[0.18em]">{SITE.dre}</p>
          </div>
        </div>
      </section>
    </div>
  );
}
